import React, { lazy } from 'react';
import { MainLayout } from '../../components/MainLayout';
import { HeroStage } from '../../components/HeroStage';
import { DualAccessGateway } from '../../components/DualAccessGateway';
import { Section } from '../../components/Section';
import { Loading } from '../../components/Loading';
import { useAvailabilityBlocks } from '../../utils/Hooks/useAvailabilityBlocks';
import { useCavallunaShows } from '../../utils/Hooks/useCavallunaShows';
import styles from '../../utils/styles/pages/Home/style.module.css';

const BioPortal = lazy(() => import('../../components/BioPortal').then((module) => ({ default: module.BioPortal })));
const ShowcaseDeck = lazy(() => import('../../components/ShowcaseDeck').then((module) => ({ default: module.ShowcaseDeck })));
const CavallunaShowsShowcase = lazy(() => import('../../components/CavallunaShowsShowcase').then((module) => ({ default: module.CavallunaShowsShowcase })));
const ContactBand = lazy(() => import('../../components/ContactBand').then((module) => ({ default: module.ContactBand })));

function scrollToSection(sectionId) {
  const node = document.getElementById(sectionId);

  if (node) {
    node.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

const biographyPortal = {
  title: 'Entrer dans le parcours de Sylvie Willms.',
  text: 'Des premières scènes aux grandes tournées, un itinéraire d’art équestre porté par le cheval et la scène.',
  cta: 'Découvrir la biographie.',
  href: 'url/biographie',
  background: '/images/cavalluna/sylvie-willms-cavalluna-portrait-fond-unie.PNG',
  subject: '/images/cavalluna/sylvie-willms-cavalluna-portrait-sujet-transparent.png'
};

const showcaseItems = [
  {
    eyebrow: 'Haute école',
    title: 'Pas espagnol et précision.',
    text: 'Le geste s’élève, la ligne se tend, et chaque détail prend sa place.',
    background: '/images/cavalluna/cavalluna-jambete-tanos-fond-unie.png',
    subject: '/images/cavalluna/cavalluna-jambete-tanos-fond-transparent-sujet.png',
    variant: 'hauteEcole'
  },
  {
    eyebrow: 'Scène',
    title: 'Tableau vivant.',
    text: 'Lumière, souffle et présence composent une image qui reste en mémoire.',
    background: '/images/numeros/Liberte/sylvie_liberte_mouvement_fond_unie.png',
    subject: '/images/numeros/Liberte/sylvie_liberte_mouvement_sujet.png',
    variant: 'scene'
  },
  {
    eyebrow: 'frison',
    title: 'Force maîtrisée.',
    text: 'La puissance avance dans le calme, portée par la confiance et l’écoute.',
    background: '/images/sylvie_profil_frison_fond_herbe_unis.png',
    subject: '/images/sylvie_profil_frison_monte_fond_herbe-transparent.png',
    variant: 'frison'
  }
];

function Home({ heatmapRenderMode = false, previewDevice = 'desktop', previewMode = false }) {
  const { shows, loading, error } = useCavallunaShows();
  const {
    availabilityBlocks,
    loading: availabilityLoading,
    error: availabilityError
  } = useAvailabilityBlocks();

  const navItems = previewMode ? [] : [
    { id: 'nav-home', label: 'Ouverture', onClick: () => scrollToSection('hero') },
    { id: 'nav-bio', label: 'Biographie', onClick: () => scrollToSection('biographie') },
    { id: 'nav-show', label: 'Réalisations', onClick: () => scrollToSection('realisations') },
    { id: 'nav-events', label: 'Dates', onClick: () => scrollToSection('actualite') },
    { id: 'nav-studio', label: 'Coulisses', onClick: () => { window.location.hash = '/studio'; } }
  ];

  return (
    <MainLayout
      frameDevice={heatmapRenderMode ? previewDevice : null}
      immersiveMode={heatmapRenderMode}
      navItems={navItems}
      previewDevice={previewDevice}
      previewMode={previewMode}
      showFooter={!previewMode}
      showHeader={false}
      showNavigation={!previewMode}
    >
      <div className={styles.page}>
        <HeroStage />
        <Section
          eyebrow="Proposition."
          id="acces"
          title="Comprendre ce que Sylvie Willms peut porter pour une scène, un événement ou un cheval."
        >
          <DualAccessGateway
            availabilityBlocks={availabilityBlocks}
            availabilityError={availabilityError}
            availabilityLoading={availabilityLoading}
            forceExpandedPanels={heatmapRenderMode}
            shows={shows}
            showsError={error}
            showsLoading={loading}
          />
        </Section>
        <Section
          eyebrow="Biographie."
          deferBody={!previewMode && !heatmapRenderMode}
          deferredFallback={<Loading label="Chargement de la biographie..." />}
          deferredMinHeight={280}
          deferredRootMargin="0px 0px 720px 0px"
          id="biographie"
          title="Parcourir la trajectoire."
        >
          <BioPortal {...biographyPortal} />
        </Section>
        <Section
          eyebrow="Réalisations."
          deferBody={!previewMode && !heatmapRenderMode}
          deferredFallback={<Loading label="Chargement des réalisations..." />}
          deferredMinHeight={360}
          deferredRootMargin="0px 0px 820px 0px"
          id="realisations"
          title="Gestes, tableaux et liberté."
        >
          <ShowcaseDeck items={showcaseItems} />
        </Section>
        <Section
          eyebrow="En ce moment."
          deferBody={!previewMode && !heatmapRenderMode}
          deferredFallback={<Loading label="Chargement des prochaines dates..." />}
          deferredMinHeight={520}
          deferredRootMargin="0px 0px 960px 0px"
          id="actualite"
          title="Les prochaines escales."
        >
          <CavallunaShowsShowcase error={error} loading={loading} shows={shows} />
        </Section>
        <Section
          eyebrow="Contact."
          deferBody={!previewMode && !heatmapRenderMode}
          deferredFallback={<Loading label="Chargement du contact..." />}
          deferredMinHeight={420}
          deferredRootMargin="0px 0px 900px 0px"
          id="contact"
          title="Entrer en contact."
        >
          <ContactBand />
        </Section>
      </div>
    </MainLayout>
  );
}

export default Home;
